import { useState, useEffect } from 'react';
import api from '../utils/api';

/**
 * Custom hook to fetch and manage coupon status data
 * @param {string} couponCode - Specific coupon code to fetch (default: 'ALPHA90')
 * @returns {object} - { couponData, loading, error, refetch }
 */
export const useCouponStatus = (couponCode = 'ALPHA90') => {
  const [couponData, setCouponData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCouponStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/coupon/status/${couponCode}`);
      setCouponData(response.data);
    } catch (err) {
      console.error('Failed to fetch coupon status:', err);
      setError(err.response?.data?.detail || 'Failed to load coupon status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCouponStatus();
  }, [couponCode]);

  return { 
    couponData, 
    loading, 
    error, 
    refetch: fetchCouponStatus 
  };
};

/**
 * Helper function to format coupon display with strikethrough
 * @param {number} total - Total number of coupons
 * @param {number} used - Number of coupons used
 * @returns {JSX.Element} - Formatted coupon text with strikethrough if applicable
 */
export const formatCouponDisplay = (total, used) => {
  const remaining = total - used;
  
  if (used === 0) {
    // No strikethrough if none used
    return <>{total} spots left!</>;
  } else if (remaining === 0) {
    // All used up
    return <><span style={{ textDecoration: 'line-through' }}>{total}</span> All spots taken!</>;
  } else {
    // Some used, show strikethrough
    return <><span style={{ textDecoration: 'line-through' }}>{total}</span> {remaining} {remaining === 1 ? 'spot' : 'spots'} left!</>;
  }
};